import { JobSectionNav } from '../components/jobs/JobSectionNav';
import { jobSections } from '../components/jobs/jobSections';
import { usePermission } from '../hooks/usePermission';
import { JobChangeOrders } from '../components/jobs/JobChangeOrders';
import { JobProfitDesk } from '../components/billing/ProfitDesk';
import { JobActivityPanel, JobNextActions, JobFinancialOverview, useCollectionPage } from '../components/jobs/JobWorkspace';
import { useEvidenceFilters, PhotoComparisons, DocumentVersions } from '../components/jobs/JobEvidenceTools';
import { JobMeasurementReview } from '../components/jobs/JobMeasurementReview';
import { JobAdditionalReps } from '../components/jobs/JobAdditionalReps';
import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  TabPanel,
  Card,
  CardContent,
  Chip,
  Select,
  Option,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
  Grid,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Tooltip,
  LinearProgress
} from '@mui/joy';
import {
  ArrowLeft,
  PencilSimple as Edit,
  Camera,
  UploadSimple as Upload,
  Plus,
  Phone,
  Envelope as Mail,
  MapPin,
  X,
  CaretRight as ChevronRight,
  Medal as Award,
  CurrencyDollar as DollarSign,
  CreditCard,
  Receipt,
  FileText,
  CheckCircle,
  ArrowCounterClockwise as UncompleteIcon,
} from '@phosphor-icons/react';
import { useJobDetail, useJobPhotos, useJobDocs, useAddJobContact, useRemoveJobContact } from '../hooks/useJobs';
import { useJobUsage } from '../hooks/useJobUsage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GroupedStatusSelect } from '../components/GroupedStatusSelect';
import { TaskCreateModal, TaskList, TaskPreview } from '../components/tasks';
import { useTaskMutations } from '../hooks/useTasks';
import type { Task as TaskType } from '../types/tasks';
import { ContactSelectionModal } from '../components/contacts';
import JobCommunication from '../components/jobs/JobCommunication';
import { QuickActionsProvider } from '../components/global';
import { EditJobModal, JobFinancialsTab, JobMaterialOrdersTab, JobDocumentGallery, SupplementReportModal } from '../components/jobs';
import { PhotoUploadModal } from '../components/PhotoUploadModal';
import { PhotoGallery } from '../components/PhotoGallery';
import { DocumentUploadModal } from '../components/documents';
import { CoCReportModal } from '../components/jobs/CoCReportModal';
import { MeasurementsList } from '../components/measurements/MeasurementsList';
import { MeasurementPdfUploadModal } from '../components/measurements/MeasurementPdfUploadModal';
import { useDetailedMeasurementsByJob } from '../hooks/measurements/useDetailedMeasurements';
import { DocumentGallery } from '../components/crm/DocumentGallery';
import { apiClient } from '../lib/api';
import { estimatesApi } from '../lib/billingApi';
import toast from 'react-hot-toast';
import { useJobBilling } from '../hooks/useBilling';
import { WorkOrderList } from '../components/subcontractors';
import { WorkOrderForm } from '../components/subcontractors';
import { Modal, ModalDialog, ModalClose } from '@mui/joy';
import { Hammer } from '@phosphor-icons/react';
import HomeownerPanel from '../components/homeowner/HomeownerPanel';

export const JobDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabNames = jobSections.map(section => section.key);
  const activeTab = Math.max(0, tabNames.indexOf(searchParams.get('tab') || 'overview'));
  const setActiveTab = (index: number) => setSearchParams(previous => { const next = new URLSearchParams(previous); next.set('tab', tabNames[index] || 'overview'); return next; });
  const [hideCompletedTasks, setHideCompletedTasks] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showContactSelectionModal, setShowContactSelectionModal] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [showCoCModal, setShowCoCModal] = useState(false);
  const [showSupplementModal, setShowSupplementModal] = useState(false);
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [showMeasurementUploadModal, setShowMeasurementUploadModal] = useState(false);

  // Get job usage data
  const { photoUsage, docUsage, refetch: refetchUsage } = useJobUsage(jobId);

  // Register Quick Actions modal handler for tasks
  React.useEffect(() => {
    console.log('JobDetail useEffect - registering task modal');
    console.log('window.QuickActions:', window.QuickActions);

    const registerTaskModal = () => {
      if (window.QuickActions) {
        console.log('Registering task modal handler');
        window.QuickActions.registerTaskModal(() => {
          console.log('Task modal handler called from JobDetail');
          setShowCreateTaskModal(true);
        });
        return true;
      }
      return false;
    };

    // Try to register immediately
    if (!registerTaskModal()) {
      // If QuickActions API isn't ready, retry after a short delay
      console.log('QuickActions API not ready, retrying in 50ms...');
      const retryTimeout = setTimeout(() => {
        if (registerTaskModal()) {
          console.log('Delayed task modal registration successful');
        } else {
          console.warn('QuickActions API still not available after retry');
        }
      }, 50);

      // Cleanup timeout if component unmounts
      return () => {
        clearTimeout(retryTimeout);
        if (window.QuickActions) {
          console.log('Unregistering task modal handler');
          window.QuickActions.unregisterTaskModal();
        }
      };
    }

    // Cleanup on unmount
    return () => {
      if (window.QuickActions) {
        console.log('Unregistering task modal handler');
        window.QuickActions.unregisterTaskModal();
      }
    };
  }, []);

  const { data: job, isLoading: jobLoading, error: jobError } = useJobDetail(jobId);
  const linkedContacts = [job?.associated_contact, ...(job?.contacts || [])]
    .filter((contact): contact is NonNullable<typeof contact> => !!contact)
    .filter((contact, index, rows) => rows.findIndex(row => row.id === contact.id) === index);
  const { data: photos = [], isLoading: photosLoading, refetch: refetchPhotos } = useJobPhotos(jobId);
  const { data: tasks = [], isLoading: tasksLoading, error: tasksError, refetch: refetchTasks } = useQuery({
    queryKey: ['jobTasks', jobId], enabled: !!jobId,
    queryFn: async () => (await apiClient.getAllPages<TaskType>(`/api/tasks/?related_job=${jobId}&ordering=due_date,id`)).data,
  });
  const { data: docs = [], isLoading: docsLoading } = useJobDocs(jobId);
  const { estimates, invoices, payments, isLoading: billingLoading, error: billingError, refetch: refetchBilling } = useJobBilling(jobId);

  // Detailed measurements for job
  const { data: detailedMeasurements = [], isLoading: detailedMeasurementsLoading } = useDetailedMeasurementsByJob(parseInt(jobId || '0'));

  // Measurement documents query (documents with measurement=true)
  const { data: measurementDocs = [], isLoading: measurementDocsLoading } = useQuery({
    queryKey: ['jobMeasurementDocs', jobId],
    queryFn: async () => {
      const response = await apiClient.getAllPages<any>(`/api/docs/?job=${jobId}&measurement=true`);
      return response.data;
    },
    enabled: !!jobId,
  });

  // Task mutations for consistent task interactions
  const { completeTask, updateStatus } = useTaskMutations();

  // Contact mutations
  const addContactMutation = useAddJobContact();
  const removeContactMutation = useRemoveJobContact();

  // Filter tasks based on hideCompletedTasks state
  const filteredTasks = hideCompletedTasks
    ? tasks.filter((task: TaskType) => task.status !== 'completed')
    : tasks;

  const taskPage = useCollectionPage(filteredTasks, 'tasks');
  const estimatePage = useCollectionPage(estimates, 'estimates');
  const invoicePage = useCollectionPage(invoices, 'invoices');
  const paymentPage = useCollectionPage(payments, 'payments');
  const detailedPage = useCollectionPage(detailedMeasurements, 'measurement revisions');
  const measurementDocPage = useCollectionPage(measurementDocs, 'measurement documents');
  const photoFilters = useEvidenceFilters(photos);
  const docFilters = useEvidenceFilters(docs);

  // Task interaction handlers
  const handleTaskClick = () => {
    // Switch to tasks tab and select task
    setActiveTab(1);
  };

  const handleTaskToggleComplete = (taskId: number, completed: boolean) => {
    if (completed) {
      completeTask.mutate(taskId);
    } else {
      updateStatus.mutate({ taskId, status: 'not_started' });
    }
  };

  const handleViewAllTasks = () => {
    setActiveTab(1);
  };

  const handleAddContact = (contactId: number) => {
    if (!jobId) return;
    addContactMutation.mutate({ jobId, contactId });
  };

  const handleRemoveContact = (contactId: number) => {
    if (!jobId) return;
    removeContactMutation.mutate({ jobId, contactId });
  };

  const priorityMutation = useMutation({
    mutationFn: async (priority: string) => (await apiClient.patch(`/api/jobs/${jobId}/`, { priority })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-activity', jobId] });
      toast.success('Job priority updated');
    },
    onError: (error: Error) => toast.error(error.message || 'Unable to update job priority.'),
  });

  // Status update mutation
  const statusUpdateMutation = useMutation({
    mutationFn: async ({ jobId, statusId }: { jobId: string; statusId: number }) => {
      const response = await apiClient.patch(`/api/jobs/${jobId}/`, { board_status: statusId });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch the job data to show updated status
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-activity', jobId] });
      toast.success('Job stage updated');
    },
    onError: (error) => {
      console.error('Error updating job status:', error);
      toast.error((error as any)?.message || 'Unable to update the job stage.');
    },
  });

  const uncompleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.markJobComplete(parseInt(id, 10), false);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      toast.success('Job uncompleted and returned to the active board');
    },
    onError: (error) => {
      console.error('Error uncompleting job:', error);
      toast.error('Failed to uncomplete job');
    },
  });

  const handleStatusChange = (statusId: number) => {
    if (!jobId) return;
    statusUpdateMutation.mutate({ jobId, statusId });
  };

  const handleUncomplete = () => {
    if (!jobId) return;
    if (!window.confirm('Uncomplete this job and return it to the active board?')) {
      return;
    }
    uncompleteMutation.mutate(jobId);
  };

  if (jobLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
        <CircularProgress size="lg" />
        <Typography level="body-md" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading job details...
        </Typography>
      </Box>
    );
  }

  if (jobError || !job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger" variant="soft">
          <Typography level="title-md">Error Loading Job</Typography>
          <Typography level="body-md" sx={{ mt: 1 }}>
            {jobError?.message || 'Job not found or you do not have permission to view it.'}
          </Typography>
          <Button
            onClick={() => navigate('/operations/jobs')}
            size="sm"
            sx={{ mt: 2 }}
          >
            Back to Jobs
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <QuickActionsProvider
      enableContactModal={true}
      onContactCreated={() => {
        // Could refresh contacts if needed
        console.log('Contact created from Quick Actions on Job Detail page');
      }}
    >
      <Box sx={{ width: '100%' }}>
      {/* Completed Banner */}
      {job.is_completed && (
        <Alert
          color="success"
          variant="solid"
          startDecorator={<CheckCircle size={18} />}
          endDecorator={
            <Button
              size="sm"
              variant="soft"
              color="neutral"
              startDecorator={<UncompleteIcon size={16} />}
              loading={uncompleteMutation.isPending}
              onClick={handleUncomplete}
            >
              Uncomplete
            </Button>
          }
          sx={{ mb: 2, mx: -3, mt: -3, borderRadius: 0, px: 3 }}
        >
          <Box>
            <Typography level="title-sm" sx={{ color: 'common.white' }}>
              Job Completed
            </Typography>
            {job.completed_at && (
              <Typography level="body-xs" sx={{ color: 'common.white', opacity: 0.85 }}>
                Completed on {new Date(job.completed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </Typography>
            )}
          </Box>
        </Alert>
      )}
      {/* Header */}
      <Box
        sx={(theme) => ({
          mb: 3,
          p: 3,
          borderRadius: 'lg',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(44px) saturate(200%)',
          WebkitBackdropFilter: 'blur(44px) saturate(200%)',
          border: '1px solid rgba(255, 255, 255, 0.7)',
          boxShadow: `${theme.shadow.md}, inset 0 1px 0 rgba(255, 255, 255, 0.65)`,
          [theme.getColorSchemeSelector('dark')]: {
            backgroundColor: 'rgba(26, 24, 42, 0.5)',
            borderColor: 'rgba(255, 255, 255, 0.14)',
            boxShadow: `${theme.shadow.md}, inset 0 1px 0 rgba(255, 255, 255, 0.10)`,
          },
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton
            onClick={() => navigate('/operations/jobs')}
            variant="plain"
            color="neutral"
            size="sm"
          >
            <ArrowLeft />
          </IconButton>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Back to Jobs
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: {xs:'column', md:'row'}, gap: 2 }}>
          <Box>
            <Typography level="h2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '32px', mb: 1 }}>
              {job.job_number} - {job.name || (job.associated_contact ?
                `${job.associated_contact.first_name} ${job.associated_contact.last_name}` :
                'No Contact')
              }
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: '16px' }}>
                <MapPin size={16} />
                <Typography level="body-md" sx={{ color: 'text.secondary', fontSize: '16px' }}>
                  {job.address_1}, {job.city}, {job.state} {job.zip_code}
                </Typography>
              </Box>
              {job.value && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: '16px' }}>
                  <DollarSign size={16} />
                  <Typography level="body-md" sx={{ color: 'text.secondary', fontSize: '16px' }}>
                    Contract Total: ${job.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => navigate(`/policy-copilot?workspace=recovery&job=${job.id}`)}>Insurance recovery</Button>
            <Dropdown>
              <MenuButton
                variant="outlined"
                color="neutral"
                startDecorator={<Award />}
              >
                Generate Report...
              </MenuButton>
              <Menu placement="bottom-start">
                <MenuItem onClick={() => setShowCoCModal(true)}>
                  <Award />
                  Certificate of Completion
                </MenuItem>
                <MenuItem onClick={() => setShowSupplementModal(true)}>
                  <FileText />
                  Supplement Report
                </MenuItem>
              </Menu>
            </Dropdown>
            {job.is_completed && (
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<UncompleteIcon />}
                loading={uncompleteMutation.isPending}
                onClick={handleUncomplete}
              >
                Uncomplete
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Edit />}
              onClick={() => setShowEditJobModal(true)}
            >
              Edit Job
            </Button>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<Camera />}
              onClick={() => setShowPhotoUploadModal(true)}
            >
              Upload Photos
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} orientation="vertical" onChange={(_, newValue) => setActiveTab(newValue as number)}
        sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '64px minmax(0, 1fr)' }, gap: 2, bgcolor: 'transparent', alignItems: 'start' }}>
        <JobSectionNav active={activeTab} onChange={setActiveTab} photos={photos.length} documents={docs.length} />
        <Box sx={{ minWidth: 0, '& > [role="tabpanel"]': { px: { xs: 0, md: 1 } } }}>
          <Typography level="h3" component="h2" sx={{ px: { xs: 0, md: 1 }, mb: 2 }}>{jobSections[activeTab].title}</Typography>

        <TabPanel value={10} sx={{ px: 2, pt: 0, pb: 2 }}>{activeTab === 10 && <JobChangeOrders jobId={Number(jobId)} baseValue={Number(job.value || 0)} />}</TabPanel>

        {/* Job Overview Tab */}
        <TabPanel value={0} sx={{ px: 2, pt: 0, pb: 2 }}>
          <JobNextActions job={job} />
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid xs={12} lg={8}>
              {/* Job Details Card */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography level="h3" sx={{ mb: 3, fontWeight: 600, fontSize: '24px' }}>
                    Job Details
                  </Typography>

                  {/* Description - Full Width */}
                  {job.description && (
                    <Box sx={{ mb: 3 }}>
                      <Typography level="title-md" sx={{ fontWeight: 500, mb: 1 }}>
                        Description
                      </Typography>
                      <Typography level="body-md" sx={{ whiteSpace: 'pre-wrap' }}>
                        {job.description}
                      </Typography>
                    </Box>
                  )}

                  <Grid container spacing={3}>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel sx={{ fontWeight: 500 }}>Status</FormLabel>
                        <GroupedStatusSelect
                          currentStatusId={job.board_status}
                          currentStatusName={job.board_status_name}
                          onStatusChange={handleStatusChange}
                          disabled={statusUpdateMutation.isPending}
                        />
                      </FormControl>
                    </Grid>

                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel sx={{ fontWeight: 500 }}>Priority</FormLabel>
                        <Select value={job.priority || 'normal'} variant="outlined" disabled={!can('jobs', 'edit') || priorityMutation.isPending} onChange={(_, value) => { if (value) priorityMutation.mutate(value); }}>
                          <Option value="low">Low</Option>
                          <Option value="normal">Normal</Option>
                          <Option value="high">High</Option>
                          <Option value="urgent">Urgent</Option>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel sx={{ fontWeight: 500 }}>Assigned To</FormLabel>
                        <Typography level="body-md" sx={{ mt: 0.5 }}>
                          {job.assigned_to ?
                            (typeof job.assigned_to === 'object' && job.assigned_to.first_name && job.assigned_to.last_name ?
                              `${job.assigned_to.first_name} ${job.assigned_to.last_name}` :
                              'Assigned (loading user details...)') :
                            'Unassigned'}
                        </Typography>
                      </FormControl>
                    </Grid>

                    <JobAdditionalReps reps={job.additional_reps} />

                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel sx={{ fontWeight: 500 }}>Build Date</FormLabel>
                        <Typography level="body-md" sx={{ mt: 0.5 }}>
                          {job.build_date ? (() => {
                            // Parse date string manually to avoid timezone issues
                            const [year, month, day] = job.build_date.split('-').map(Number);
                            const date = new Date(year, month - 1, day); // month is 0-indexed
                            return date.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            });
                          })() : 'Not set'}
                        </Typography>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Tags - Read only display */}
                  {job.tags && job.tags.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 500, mb: 1 }}>Tags</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {job.tags.map((tag) => (
                          <Chip
                            key={tag.id}
                            variant="solid"
                            size="sm"
                            sx={{
                              backgroundColor: tag.color,
                              color: '#fff',
                            }}
                          >
                            {tag.name}
                          </Chip>
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Tasks Preview */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                      Tasks
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant={hideCompletedTasks ? "solid" : "outlined"}
                        size="sm"
                        onClick={() => setHideCompletedTasks(!hideCompletedTasks)}
                      >
                        {hideCompletedTasks ? 'Show All' : 'Hide Completed'}
                      </Button>
                      <Button
                        size="sm"
                        startDecorator={<Plus />}
                        onClick={() => setShowCreateTaskModal(true)}
                      >
                        Add Task
                      </Button>
                    </Box>
                  </Box>

                  <TaskPreview
                    tasks={taskPage.rows}
                    isLoading={tasksLoading}
                    onTaskClick={handleTaskClick}
                    onToggleComplete={handleTaskToggleComplete}
                    onViewAll={handleViewAllTasks}
                    maxTasks={5}
                  />
                </CardContent>
              </Card>

              {/* Photos Preview */}
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                      Photos ({photos.length})
                    </Typography>
                    <Button
                      size="sm"
                      startDecorator={<Camera />}
                      onClick={() => setShowPhotoUploadModal(true)}
                    >
                      Upload Photos
                    </Button>
                  </Box>

                  {/* Use PhotoGallery component but limit to preview */}
                  {photos.length > 0 ? (
                    <>
                      {/* Show limited preview using PhotoGallery */}
                      <Box sx={{
                        ...(photos.length > 4 && {
                          maxHeight: '400px',
                          overflow: 'hidden',
                          position: 'relative',
                        })
                      }}>
                        {/* Overview preview only shows the first few photos, so
                            no jobId here: reordering a partial list would
                            strand the photos it leaves out. */}
                        <PhotoGallery
                          photos={photos.slice(0, photos.length > 4 ? 3 : 4)}
                          loading={photosLoading}
                          onPhotoUpdate={refetchPhotos}
                          onPhotoDelete={refetchPhotos}
                        />
                      </Box>

                      {/* View All Photos button when there are more than 4 photos */}
                      {photos.length > 4 && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Button
                            variant="soft"
                            color="primary"
                            onClick={() => setActiveTab(2)}
                            endDecorator={<ChevronRight />}
                          >
                            View All {photos.length} Photos
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    !photosLoading && (
                      <Typography level="body-md" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                        No photos uploaded yet
                      </Typography>
                    )
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column */}
            <Grid xs={12} lg={4}>
              {/* Associated Contacts */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography level="h4" sx={{ mb: 3, fontWeight: 600, fontSize: '20px' }}>
                    Associated Contacts
                  </Typography>

                  {linkedContacts.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {linkedContacts.map((contact) => (
                        <Box key={contact.id} sx={{ p: 2, borderRadius: 1, bgcolor: 'background.level1', position: 'relative' }}>
                          {can('jobs', 'edit') && job.associated_contact?.id !== contact.id ? <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                            onClick={() => handleRemoveContact(contact.id)}
                          >
                            <X size={16} />
                          </IconButton> : null}
                          <Typography level="title-md" sx={{ fontWeight: 600, mb: 1 }}>
                            {contact.first_name} {contact.last_name}
                          </Typography>
                          {job.associated_contact?.id === contact.id && (
                            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                              Primary Contact
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {contact.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Mail size={16} />
                                <Typography level="body-sm">{contact.email}</Typography>
                              </Box>
                            )}
                            {contact.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Phone size={16} />
                                <Typography level="body-sm">{contact.phone}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
            color="primary"
                        size="sm"
                        startDecorator={<Plus />}
                        onClick={() => setShowContactSelectionModal(true)}
                      >
                        Add Contact
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography level="body-md" sx={{ color: 'text.secondary', mb: 2 }}>
                        No contacts assigned
                      </Typography>
                      <Button
                        size="sm"
                        startDecorator={<Plus />}
                        onClick={() => setShowContactSelectionModal(true)}
                      >
                        Add Contact
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <JobActivityPanel jobId={jobId!} />
              {/* Project Chat */}
              <JobCommunication
                job={job}
                onSendCommunication={(data) => {
                  // Optionally handle successful communication send
                  console.log('Communication sent:', data);
                }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={1} sx={{ px: 2, pt: 0, pb: 2 }}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                  Tasks ({tasks.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant={hideCompletedTasks ? "solid" : "outlined"}
                    size="sm"
                    onClick={() => setHideCompletedTasks(!hideCompletedTasks)}
                  >
                    {hideCompletedTasks ? 'Show All' : 'Hide Completed'}
                  </Button>
                  <Button
                    size="sm"
                    startDecorator={<Plus />}
                    onClick={() => setShowCreateTaskModal(true)}
                  >
                    Add Task
                  </Button>
                </Box>
              </Box>

<>{tasksError&&<Alert color="danger" endDecorator={<Button onClick={()=>refetchTasks()}>Retry</Button>}>Could not load job tasks.</Alert>}</>
              {taskPage.controls}
              <TaskList
                tasks={taskPage.rows}
                isLoading={tasksLoading}
                onTaskUpdate={() => {
                  // Invalidate the tasks query to refresh the list
                  queryClient.invalidateQueries({ queryKey: ['jobTasks', jobId] });
                }}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Work Orders Tab */}
        <TabPanel value={2} sx={{ px: 2, pt: 0, pb: 2 }}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                  Work Orders
                </Typography>
                <Button
                  startDecorator={<Hammer />}
                  onClick={() => setShowWorkOrderModal(true)}
                >
                  Create Work Order
                </Button>
              </Box>

              <WorkOrderList
                jobId={parseInt(jobId || '0')}
                showCreateButton={false}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Photos Tab */}
        <TabPanel value={3} sx={{ px: 2, pt: 0, pb: 2 }}>
          {photoFilters.controls}<PhotoComparisons jobId={jobId!} photos={photos} />
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                    Photos
                  </Typography>
                  {photoUsage && (
                    <Chip
                      size="sm"
                      color={
                        photoUsage.is_at_limit ? 'danger' :
                        photoUsage.percentage_used >= 80 ? 'warning' :
                        'success'
                      }
                      variant="soft"
                    >
                      <Tooltip
                        title={
                          <Box>
                            <Typography level="body-sm" fontWeight="lg">
                              Photo Storage Usage
                            </Typography>
                            <Typography level="body-xs">
                              {photoUsage.current_usage} of {photoUsage.limit === 'Unlimited' ? 'unlimited' : photoUsage.limit} photos used
                            </Typography>
                            {photoUsage.percentage_used > 0 && photoUsage.limit !== 'Unlimited' && (
                              <LinearProgress
                                determinate
                                value={Math.min(photoUsage.percentage_used, 100)}
                                sx={{ mt: 1, height: 4 }}
                                color={
                                  photoUsage.is_at_limit ? 'danger' :
                                  photoUsage.percentage_used >= 80 ? 'warning' :
                                  'success'
                                }
                              />
                            )}
                          </Box>
                        }
                        placement="bottom"
                      >
                        <span style={{ cursor: 'help' }}>
                          {photoUsage.current_usage}/{photoUsage.limit === 'Unlimited' ? '∞' : photoUsage.limit}
                        </span>
                      </Tooltip>
                    </Chip>
                  )}
                </Box>
                <Button
                  startDecorator={<Camera />}
                  onClick={() => setShowPhotoUploadModal(true)}
                >
                  Upload Photos
                </Button>
              </Box>

              <PhotoGallery
                photos={photoFilters.filtered}
                allowReorder={photoFilters.filtered.length === photos.length}
                loading={photosLoading}
                jobId={job.id}
                onPhotoUpdate={() => {
                  refetchPhotos();
                  refetchUsage(); // Update usage counts
                }}
                onPhotoDelete={() => {
                  refetchPhotos();
                  refetchUsage(); // Update usage counts
                }}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={4} sx={{ px: 2, pt: 0, pb: 2 }}>
          {docFilters.controls}<DocumentVersions jobId={jobId!} documents={docs} />
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                    Documents
                  </Typography>
                  {docUsage && (
                    <Chip
                      size="sm"
                      color={
                        docUsage.is_at_limit ? 'danger' :
                        docUsage.percentage_used >= 80 ? 'warning' :
                        'success'
                      }
                      variant="soft"
                    >
                      <Tooltip
                        title={
                          <Box>
                            <Typography level="body-sm" fontWeight="lg">
                              Document Storage Usage
                            </Typography>
                            <Typography level="body-xs">
                              {docUsage.current_usage} of {docUsage.limit === 'Unlimited' ? 'unlimited' : docUsage.limit} documents used
                            </Typography>
                            {docUsage.percentage_used > 0 && docUsage.limit !== 'Unlimited' && (
                              <LinearProgress
                                determinate
                                value={Math.min(docUsage.percentage_used, 100)}
                                sx={{ mt: 1, height: 4 }}
                                color={
                                  docUsage.is_at_limit ? 'danger' :
                                  docUsage.percentage_used >= 80 ? 'warning' :
                                  'success'
                                }
                              />
                            )}
                          </Box>
                        }
                        placement="bottom"
                      >
                        <span style={{ cursor: 'help' }}>
                          {docUsage.current_usage}/{docUsage.limit === 'Unlimited' ? '∞' : docUsage.limit}
                        </span>
                      </Tooltip>
                    </Chip>
                  )}
                </Box>
                <Button
                  startDecorator={<Upload />}
                  onClick={() => setShowDocumentUploadModal(true)}
                >
                  Upload Document
                </Button>
              </Box>

              <JobDocumentGallery
                folderParent={{ job: Number(jobId) }}
                documents={docFilters.filtered}
                loading={docsLoading}
                onDocumentUpdate={() => {
                  queryClient.invalidateQueries({ queryKey: ['jobDocs', jobId] });
                  refetchUsage(); // Update usage counts
                }}
                onDocumentDelete={() => {
                  queryClient.invalidateQueries({ queryKey: ['jobDocs', jobId] });
                  refetchUsage(); // Update usage counts
                }}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Billing Tab */}
        <TabPanel value={5} sx={{ px: 2, pt: 0, pb: 2 }}>
          <JobFinancialOverview jobId={jobId!} onTab={setActiveTab} />
          {billingError&&<Alert color="danger" endDecorator={<Button onClick={()=>refetchBilling()}>Retry</Button>}>Could not load billing records.</Alert>}
          <Grid container spacing={3}>
            {/* Estimates Section */}
            <Grid xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                      Estimates ({estimates.length})
                    </Typography>
                    <Button
                      startDecorator={<DollarSign />}
                      onClick={() => window.open(`/billing/estimates/new?job=${jobId}`, '_blank')}
                    >
                      Create Estimate
                    </Button>
                  </Box>

                  {billingLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : estimates.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                        No estimates created yet
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {estimatePage.controls}
                      {estimatePage.rows.map((estimate) => (
                        <Card key={estimate.id} variant="soft">
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                              <Typography level="title-sm">
                                Estimate #{estimate.document_number}
                              </Typography>
                              <Chip
                                size="sm"
                                variant="outlined"
                                color={
                                  estimate.status === 'sent' ? 'primary' :
                                  estimate.status === 'draft' ? 'neutral' :
                                  estimate.status === 'viewed' ? 'success' :
                                  estimate.acceptance_status === 'accepted' ? 'success' :
                                  estimate.acceptance_status === 'rejected' ? 'danger' :
                                  'neutral'
                                }
                              >
                                {estimate.acceptance_status || estimate.status}
                              </Chip>
                            </Box>
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                              {estimate.customer_name || 'No customer'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography level="body-sm" fontWeight="lg">
                                ${parseFloat(estimate.total_amount || '0').toFixed(2)}
                              </Typography>
                              <Typography level="body-xs" textColor="text.secondary">
                                Created {new Date(estimate.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                            {estimate.valid_until && (
                              <Typography level="body-xs" textColor="text.secondary" sx={{ mt: 1 }}>
                                Valid until {new Date(estimate.valid_until).toLocaleDateString()}
                              </Typography>
                            )}
                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                              <Button
                                size="sm"
                                variant="outlined"
                                color="primary"
                                onClick={() => window.open(`/billing/estimates/${estimate.id}`, '_blank')}
                              >
                                View Estimate
                              </Button>
                              {estimate.acceptance_status === 'accepted' && !estimate.converted_to_invoice_id && (
                                <Button
                                  size="sm"
                                  variant="solid"
                                  color="success"
                                  onClick={async () => {
                                    try {
                                      const data = await estimatesApi.convertToInvoice(estimate.id);
                                      const invoiceId = data?.invoice?.id ?? data?.invoice_id;
                                      queryClient.invalidateQueries({ queryKey: ['jobEstimates', jobId] });
                                      queryClient.invalidateQueries({ queryKey: ['jobInvoices', jobId] });
                                      toast.success('Estimate converted to invoice');
                                      if (invoiceId) {
                                        navigate(`/operations/billing/invoices/${invoiceId}`);
                                      }
                                    } catch (error) {
                                      toast.error((error as any)?.error || 'Failed to convert estimate to invoice');
                                    }
                                  }}
                                >
                                  Convert to Invoice
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Invoices Section */}
            <Grid xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                      Invoices ({invoices.length})
                    </Typography>
                    <Button
                      startDecorator={<Receipt />}
                      onClick={() => window.open(`/billing/invoices/new?job=${jobId}`, '_blank')}
                    >
                      Create Invoice
                    </Button>
                  </Box>

                  {billingLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : invoices.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                        No invoices created yet
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {invoicePage.controls}
                      {invoicePage.rows.map((invoice) => (
                        <Card key={invoice.id} variant="soft">
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                              <Typography level="title-sm">
                                Invoice #{invoice.document_number}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  size="sm"
                                  variant="outlined"
                                  color={
                                    invoice.status === 'sent' ? 'primary' :
                                    invoice.status === 'draft' ? 'neutral' :
                                    invoice.status === 'viewed' ? 'success' :
                                    'neutral'
                                  }
                                >
                                  {invoice.status}
                                </Chip>
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  color={
                                    invoice.payment_status === 'paid' ? 'success' :
                                    invoice.payment_status === 'partial' ? 'warning' :
                                    invoice.payment_status === 'overdue' ? 'danger' :
                                    'neutral'
                                  }
                                >
                                  {invoice.payment_status}
                                </Chip>
                              </Box>
                            </Box>
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                              {invoice.customer_name || 'No customer'}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1 }}>
                              <Box>
                                <Typography level="body-xs" textColor="text.secondary">
                                  Total Amount
                                </Typography>
                                <Typography level="body-sm" fontWeight="lg">
                                  ${parseFloat(invoice.total_amount || '0').toFixed(2)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography level="body-xs" textColor="text.secondary">
                                  Balance Due
                                </Typography>
                                <Typography level="body-sm" fontWeight="lg" color={parseFloat(invoice.balance_due) > 0 ? 'danger' : 'success'}>
                                  ${parseFloat(invoice.balance_due || '0').toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography level="body-xs" textColor="text.secondary">
                                Due: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'Not set'}
                              </Typography>
                              <Typography level="body-xs" textColor="text.secondary">
                                Created {new Date(invoice.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                              <Button
                                size="sm"
                                variant="outlined"
                                color="primary"
                                onClick={() => window.open(`/billing/invoices/${invoice.id}`, '_blank')}
                              >
                                View Invoice
                              </Button>
                              {parseFloat(invoice.balance_due) > 0 && (
                                <Button
                                  size="sm"
                                  variant="solid"
                                  color="primary"
                                  startDecorator={<CreditCard size={16} />}
                                  onClick={() => window.open(`/billing/invoices/${invoice.id}/payment`, '_blank')}
                                >
                                  Record Payment
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Payments Section */}
            <Grid xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '24px' }}>
                      Payments ({payments.length})
                    </Typography>
                  </Box>

                  {billingLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : payments.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                        No payments recorded yet
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {paymentPage.controls}
                      {paymentPage.rows.map((payment) => (
                        <Card key={payment.id} variant="soft">
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                              <Box>
                                <Typography level="title-sm">
                                  Payment #{payment.id}
                                </Typography>
                                {payment.invoice && (
                                  <Typography level="body-xs" textColor="text.secondary">
                                    For Invoice #{payment.invoice.document_number}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                size="sm"
                                variant="soft"
                                color="success"
                              >
                                {payment.payment_method}
                              </Chip>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography level="body-lg" fontWeight="lg" color="success">
                                ${parseFloat(payment.amount || '0').toFixed(2)}
                              </Typography>
                              <Typography level="body-xs" textColor="text.secondary">
                                {new Date(payment.payment_date).toLocaleDateString()}
                              </Typography>
                            </Box>
                            {payment.reference_number && (
                              <Typography level="body-xs" textColor="text.secondary" sx={{ mt: 1 }}>
                                Ref: {payment.reference_number}
                              </Typography>
                            )}
                            {payment.notes && (
                              <Typography level="body-sm" sx={{ mt: 1 }}>
                                {payment.notes}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Measurements Tab */}
        <TabPanel value={6} sx={{ px: 2, pt: 0, pb: 2 }}>
          <JobMeasurementReview job={job} detailed={detailedMeasurements} />
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography level="title-md" startDecorator={<MapPin size={16} />}>
                  Uploaded Measurements
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<MapPin size={16} />}
                    onClick={() => navigate(`/operations/measurements?jobId=${jobId}`)}
                  >
                    Create Quick Measure
                  </Button>
                  <Button
                    size="sm"
                    variant="solid"
                    startDecorator={<Upload size={16} />}
                    onClick={() => setShowMeasurementUploadModal(true)}
                  >
                    Upload Measurements
                  </Button>
                </Box>
              </Box>

              {/* Parsed Measurement Data Section */}
              {detailedMeasurementsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size="sm" />
                </Box>
              ) : detailedMeasurements.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                    Measurement Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {detailedPage.controls}
                      {detailedPage.rows.map((dm) => (
                      <Card key={dm.id} variant="soft" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography level="title-sm">
                              {dm.source_type_display}
                            </Typography>
                            <Chip
                              size="sm"
                              color={dm.parsing_status === 'completed' ? 'success' : dm.parsing_status === 'failed' ? 'danger' : 'neutral'}
                              variant="soft"
                            >
                              {dm.parsing_status_display}
                            </Chip>
                          </Box>
                          {dm.source_document_url && (
                            <Button
                              size="sm"
                              variant="plain"
                              component="a"
                              href={dm.source_document_url}
                              target="_blank"
                              startDecorator={<FileText size={14} />}
                            >
                              View PDF
                            </Button>
                          )}
                        </Box>
                        {dm.parsing_status === 'completed' && (
                          <Grid container spacing={2}>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Total Area</Typography>
                              <Typography level="body-sm" fontWeight="lg">
                                {dm.total_area_sqft?.toLocaleString() ?? '-'} sq ft
                              </Typography>
                            </Grid>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Squares</Typography>
                              <Typography level="body-sm" fontWeight="lg">
                                {dm.total_squares?.toFixed(1) ?? '-'}
                              </Typography>
                            </Grid>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Pitch</Typography>
                              <Typography level="body-sm" fontWeight="lg">
                                {dm.predominant_pitch || '-'}
                              </Typography>
                            </Grid>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Facets</Typography>
                              <Typography level="body-sm" fontWeight="lg">
                                {dm.facet_count ?? '-'}
                              </Typography>
                            </Grid>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Waste Factor</Typography>
                              <Typography level="body-sm" fontWeight="lg">
                                {dm.waste_factor ? `${dm.waste_factor}%` : '-'}
                              </Typography>
                            </Grid>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Ridges</Typography>
                              <Typography level="body-sm">{dm.total_ridges_ft?.toLocaleString() ?? '-'} ft</Typography>
                            </Grid>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Valleys</Typography>
                              <Typography level="body-sm">{dm.total_valleys_ft?.toLocaleString() ?? '-'} ft</Typography>
                            </Grid>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Eaves</Typography>
                              <Typography level="body-sm">{dm.total_eaves_ft?.toLocaleString() ?? '-'} ft</Typography>
                            </Grid>
                            <Grid xs={6} sm={3}>
                              <Typography level="body-xs" textColor="text.tertiary">Rakes</Typography>
                              <Typography level="body-sm">{dm.total_rakes_ft?.toLocaleString() ?? '-'} ft</Typography>
                            </Grid>
                          </Grid>
                        )}
                        {dm.parsing_status === 'failed' && dm.parsing_error && (
                          <Typography level="body-sm" color="danger">
                            Error: {dm.parsing_error}
                          </Typography>
                        )}
                      </Card>
                    ))}
                  </Box>
                </Box>
              ) : null}

              {/* Measurement Documents Section */}
              {measurementDocsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size="sm" />
                </Box>
              ) : measurementDocs.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                    Uploaded Measurement Files ({measurementDocs.length})
                  </Typography>
                  {measurementDocPage.controls}
                  <DocumentGallery
                    documents={measurementDocPage.rows}
                    onRefresh={() => {
                      queryClient.invalidateQueries({ queryKey: ['jobMeasurementDocs', jobId] });
                    }}
                  />
                </Box>
              ) : null}

              {/* Quick Measurements List */}
              <MeasurementsList
                selectedJobId={parseInt(jobId || '0')}
                onViewMeasurement={(measurement) => {
                  navigate(`/operations/measurements/${measurement.id}`);
                }}
                hideEmptyState={measurementDocs.length > 0 || detailedMeasurements.length > 0}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Financials Tab */}
        <TabPanel value={7} sx={{ px: 2, pt: 0, pb: 2 }}>
          <JobProfitDesk jobId={jobId!} />
          <JobFinancialsTab jobId={jobId!} showSummary={false} />
        </TabPanel>

        {/* Material Orders Tab (appended — tabs are positional, never renumber) */}
        <TabPanel value={8} sx={{ px: 2, pt: 0, pb: 2 }}>
          <JobMaterialOrdersTab jobId={jobId!} />
        </TabPanel>
        <TabPanel value={9} sx={{ px: 2, pt: 0, pb: 2 }}>
          {activeTab === 9 && <HomeownerPanel jobId={jobId!} />}
        </TabPanel>

        </Box>
      </Tabs>

      {/* Task Create Modal */}
      <TaskCreateModal
        open={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        onSuccess={() => {
          // Tasks auto-refresh via shared hooks
          setShowCreateTaskModal(false);
        }}
        prefilledJobId={jobId ? parseInt(jobId) : undefined}
      />

      {/* Contact Selection Modal */}
      <ContactSelectionModal
        open={showContactSelectionModal}
        onClose={() => setShowContactSelectionModal(false)}
        onContactSelect={handleAddContact}
        existingContactIds={job?.contacts?.map(c => c.id) || []}
      />

      {/* Edit Job Modal */}
      <EditJobModal
        open={showEditJobModal}
        onClose={() => setShowEditJobModal(false)}
        job={job}
      />

      {/* Photo Upload Modal */}
      {jobId && (
        <PhotoUploadModal
          open={showPhotoUploadModal}
          onClose={() => {
            setShowPhotoUploadModal(false);
            refetchPhotos(); // Refresh photo list
            refetchUsage(); // Update usage counts
          }}
          jobId={jobId}
        />
      )}

      {/* Measurement Upload Modal */}
      {jobId && (
        <MeasurementPdfUploadModal
          open={showMeasurementUploadModal}
          onClose={() => {
            setShowMeasurementUploadModal(false);
            queryClient.invalidateQueries({ queryKey: ['jobMeasurementDocs', jobId] });
            queryClient.invalidateQueries({ queryKey: ['detailedMeasurementsByJob', parseInt(jobId)] });
          }}
          jobId={parseInt(jobId)}
        />
      )}

      {/* Document Upload Modal */}
      {jobId && (
        <DocumentUploadModal
          open={showDocumentUploadModal}
          onClose={() => {
            setShowDocumentUploadModal(false);
            queryClient.invalidateQueries({ queryKey: ['jobDocs', jobId] }); // Refresh doc list
            refetchUsage(); // Update usage counts
          }}
          jobId={parseInt(jobId)}
        />
      )}

      {/* Certificate of Completion Modal */}
      {jobId && job && (
        <CoCReportModal
          open={showCoCModal}
          onClose={() => setShowCoCModal(false)}
          job={job}
        />
      )}

      {/* Supplement Report Modal */}
      {jobId && job && (
        <SupplementReportModal
          open={showSupplementModal}
          onClose={() => setShowSupplementModal(false)}
          job={job}
        />
      )}

      {/* Work Order Creation Modal */}
      <Modal open={showWorkOrderModal} onClose={() => setShowWorkOrderModal(false)}>
        <ModalDialog sx={{ width: '90%', maxWidth: '800px', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h3" sx={{ mb: 3 }}>Create Work Order</Typography>
          <WorkOrderForm
            jobId={parseInt(jobId || '0')}
            onSuccess={() => {
              setShowWorkOrderModal(false);
              queryClient.invalidateQueries({ queryKey: ['work-orders'] });
            }}
            onCancel={() => setShowWorkOrderModal(false)}
          />
        </ModalDialog>
      </Modal>
    </Box>
    </QuickActionsProvider>
  );
};
